/* TWRD search overlay.
   The header search field and the mobile search icon become triggers for this
   panel. Results come from the theme's own predictive endpoint
   (/search/suggest.json), so there is no second search engine. */
(function () {
  'use strict';

  var RV_KEY = 'twrd_recently_viewed'; /* same key as snippets/twrd-recently-viewed.liquid */
  var RV_MAX = 12;
  var RV_SHOWN = 12;
  var DEBOUNCE = 200;
  var MIN_CHARS = 2;

  var root = null;
  var els = {};
  var lastFocus = null;
  var restoring = false; /* true while close() hands focus back to the header trigger */
  var inlineHost = null;
  var panelHome = null;
  var timer = null;
  var reqId = 0;
  var cardTemplate = null;
  var PRODUCT_JSON = {}; /* handle -> promise of /products/<handle>.js, recently viewed only */
  var SUGGEST = {}; /* query -> {t, p}, reused for SUGGEST_TTL */
  var SUGGEST_TTL = 60000;
  var SUGGEST_MAX = 40;

  /* ---------------- helpers ---------------- */

  function productJson(handle) {
    if (!PRODUCT_JSON[handle]) {
      PRODUCT_JSON[handle] = fetch('/products/' + encodeURIComponent(handle) + '.js')
        .then(function (r) {
          if (!r.ok) throw new Error('HTTP ' + r.status);
          return r.json();
        })
        .catch(function (error) {
          delete PRODUCT_JSON[handle];
          throw error;
        });
    }
    return PRODUCT_JSON[handle];
  }

  function suggestJson(key, url) {
    var hit = SUGGEST[key];
    if (hit && Date.now() - hit.t < SUGGEST_TTL) return hit.p;
    var keys = Object.keys(SUGGEST);
    if (keys.length >= SUGGEST_MAX) {
      keys.sort(function (a, b) {
        return SUGGEST[a].t - SUGGEST[b].t;
      });
      delete SUGGEST[keys[0]];
    }
    var entry = {
      t: Date.now(),
      p: fetch(url)
        .then(function (r) {
          if (!r.ok) throw new Error('search suggest failed: ' + r.status);
          return r.json();
        })
        .catch(function (error) {
          if (SUGGEST[key] === entry) delete SUGGEST[key];
          throw error;
        })
    };
    SUGGEST[key] = entry;
    return entry.p;
  }

  /* Recently viewed is stored only with preferences consent. The rule is the
     one in assets/twrd-recent.js (window.twrdRecentAllowed); the copy below is
     the same check for the moment before that script has run. */
  function rvAllowed() {
    if (typeof window.twrdRecentAllowed === 'function') {
      try {
        return window.twrdRecentAllowed() === true;
      } catch (error) {
        return false;
      }
    }
    var cp = window.Shopify && window.Shopify.customerPrivacy;
    try {
      if (window.twrdConsent && typeof window.twrdConsent.held === 'function' && window.twrdConsent.held()) return false;
      return !!(cp && typeof cp.preferencesProcessingAllowed === 'function' && cp.preferencesProcessingAllowed() === true);
    } catch (error) {
      return false;
    }
  }

  function readRv() {
    if (!rvAllowed()) return [];
    try {
      var list = JSON.parse(localStorage.getItem(RV_KEY) || '[]');
      if (!Array.isArray(list)) return [];
      return list.filter(function (h) {
        return typeof h === 'string' && h.length > 0;
      });
    } catch (error) {
      return [];
    }
  }

  function writeRv(list) {
    if (!rvAllowed()) return;
    try {
      localStorage.setItem(RV_KEY, JSON.stringify(list));
    } catch (error) {
      /* storage can be full or blocked; recently viewed is optional */
    }
  }

  function currentProductHandle() {
    var match = window.location.pathname.match(/\/products\/([^/?#]+)/);
    return match ? decodeURIComponent(match[1]) : '';
  }

  var tracked = false;

  function trackCurrentProduct() {
    var handle = currentProductHandle();
    if (!handle || tracked || !rvAllowed()) return;
    tracked = true;
    var list = readRv().filter(function (h) {
      return h !== handle;
    });
    list.unshift(handle);
    writeRv(list.slice(0, RV_MAX));
  }

  function formatMoney(cents) {
    var amount = Number(cents);
    return isFinite(amount) ? window.twrdFormatMoney(amount) : '';
  }

  function escapeHtml(value) {
    return String(value).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function highlight(text, query) {
    var safe = escapeHtml(text);
    var index = text.toLowerCase().indexOf(query.toLowerCase());
    if (index < 0 || !query) return safe;
    return (
      escapeHtml(text.slice(0, index)) +
      '<b>' +
      escapeHtml(text.slice(index, index + query.length)) +
      '</b>' +
      escapeHtml(text.slice(index + query.length))
    );
  }

  function clear(node) {
    while (node && node.firstChild) node.removeChild(node.firstChild);
  }

  /* ---------------- card rendering ---------------- */

  /* The scripture reference sits in the description as "— John 1:1"
     (snippets/twrd-verse-ref.liquid is the server-side parser). Nothing is
     shown when the text holds no reference. */
  var VERSE_RE = /[\u2014\u2013-]\s*((?:[1-3]\s)?[A-Z][a-z]+(?:\s(?:of\s)?[A-Z][a-z]+)?\s\d{1,3}:\d{1,3}(?:\s?[\u2013-]\s?\d{1,3})?)/;

  function verseFrom(html, type) {
    var text = String(html || '')
      .replace(/<br\s*\/?>/gi, ' ')
      .replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;/g, ' ')
      .replace(/&mdash;/g, '\u2014')
      .replace(/&ndash;/g, '\u2013');
    var m = text.match(VERSE_RE);
    if (m) return m[1].replace(/\s+/g, ' ').trim();
    /* Sweatpants carry the cross, not a verse (same rule as the store card). */
    if (String(type || '').toLowerCase().indexOf('sweatpant') >= 0) return 'The Cross';
    return '';
  }

  function hasTag(tags, name) {
    var list = Array.isArray(tags) ? tags : String(tags || '').split(',');
    return list.some(function (t) {
      return String(t).trim().toLowerCase() === name;
    });
  }

  function buildCard(data) {
    if (!cardTemplate) return null;
    var frag = cardTemplate.content.cloneNode(true);
    var link = frag.querySelector('.twrd-search-card');
    var img = frag.querySelector('[data-card-img]');
    var pill = frag.querySelector('[data-card-pill]');
    var title = frag.querySelector('[data-card-title]');
    var verse = frag.querySelector('[data-card-verse]');
    var price = frag.querySelector('[data-card-price]');

    link.setAttribute('href', data.url || '#');
    if (data.image) {
      img.setAttribute('src', data.image);
      img.setAttribute('alt', data.title || '');
    } else {
      img.remove();
    }
    if (data.isNew) pill.removeAttribute('hidden');
    title.textContent = data.title || '';
    if (data.verse && verse) {
      verse.textContent = data.verse;
      verse.removeAttribute('hidden');
    } else if (verse) {
      verse.remove();
    }
    price.textContent = data.upcoming ? 'Coming soon' : data.price || '';
    return frag;
  }

  /* TWRD 2026-10-21: UPCOMING pieces show "Coming soon", never a price.
     The tag comes from the product JSON; the handle list from snippets/twrd-upcoming-guard.liquid. */
  function isUpcoming(handle, tags) {
    if (hasTag(tags, 'twrd-upcoming')) return true;
    var list = (window.twrdUpcoming && window.twrdUpcoming.handles) || [];
    return !!handle && list.indexOf(handle) !== -1;
  }

  function handleFromUrl(url) {
    var m = String(url || '').match(/\/products\/([^/?#]+)/);
    return m ? m[1] : '';
  }

  function sizedImage(url, width) {
    if (!url) return '';
    return url.replace(/(\.(?:png|jpe?g|webp|gif))(\?.*)?$/i, '_' + width + 'x$1$2');
  }

  function fromProductJson(product) {
    var tags = product.tags || [];
    return {
      url: '/products/' + product.handle,
      image: sizedImage(product.featured_image || (product.images && product.images[0]) || '', 400),
      title: product.title || '',
      verse: verseFrom(product.description, product.type),
      isNew: hasTag(tags, 'new'),
      price: formatMoney(product.price),
      upcoming: isUpcoming(product.handle, tags)
    };
  }

  function fromSuggest(product) {
    var image = '';
    if (product.featured_image && product.featured_image.url) image = product.featured_image.url;
    else if (product.image) image = product.image;
    var cents = Math.round(parseFloat(String(product.price).replace(/[^0-9.]/g, '')) * 100);
    /* suggest.json carries type, tags and body, so no extra request per card. */
    return {
      url: product.url || '',
      image: sizedImage(image, 400),
      title: product.title || '',
      verse: verseFrom(product.body, product.type),
      isNew: hasTag(product.tags, 'new'),
      price: isFinite(cents) ? formatMoney(cents) : '',
      upcoming: isUpcoming(handleFromUrl(product.url), product.tags)
    };
  }

  /* ---------------- recently viewed ---------------- */

  function renderRecentlyViewed() {
    if (els.keep) {
      /* Collection pages render "Keep shopping for" server-side. */
      els.rv.hidden = true;
      els.fallback.hidden = true;
      return;
    }
    var handles = readRv()
      .filter(function (h) {
        return h !== currentProductHandle();
      })
      .slice(0, RV_SHOWN);

    if (!handles.length) {
      els.rv.hidden = true;
      els.fallback.hidden = false;
      return;
    }

    Promise.all(
      handles.map(function (handle) {
        return productJson(handle).catch(function () {
          return null;
        });
      })
    ).then(function (products) {
      clear(els.rail);
      var count = 0;
      products.forEach(function (product) {
        if (!product) return;
        var card = buildCard(fromProductJson(product));
        if (card) {
          els.rail.appendChild(card);
          count++;
        }
      });
      if (count) {
        els.rv.hidden = false;
        els.fallback.hidden = true;
      } else {
        els.rv.hidden = true;
        els.fallback.hidden = false;
      }
      updateRailNavs();
    });
  }

  function updateRailNavs() {
    if (!els.rail || !els.railPrev) return;
    var max = els.rail.scrollWidth - els.rail.clientWidth - 1;
    /* Nothing to scroll: no arrows. */
    if (els.navs) els.navs.hidden = max <= 0;
    els.railPrev.disabled = els.rail.scrollLeft <= 0;
    els.railNext.disabled = els.rail.scrollLeft >= max;
  }

  /* ---------------- guides and help ----------------
     Static quick answers from twrd-search-guides.js. Pages from predictive
     search are listed in the Pages group under the products. */

  var GUIDES_MAX = 3;

  function linkKey(url) {
    try {
      var parsed = new URL(url, window.location.origin);
      return (parsed.pathname + parsed.hash).toLowerCase();
    } catch (error) {
      return String(url).toLowerCase();
    }
  }

  function guideLinks(query) {
    var api = window.twrdSearchGuides;
    var fixed = api && typeof api.match === 'function' ? api.match(query) : [];
    var seen = {};
    return fixed
      .filter(function (link) {
        var key = linkKey(link.url);
        if (seen[key]) return false;
        seen[key] = true;
        return true;
      })
      .slice(0, GUIDES_MAX);
  }

  /* skip: paths the Pages group already lists. A plain page link (no hash)
     to one of them is left out here, so each page shows once, under Pages. */
  function renderGuides(query, skip) {
    if (!els.guides) return [];
    var skipPaths = (skip || []).map(function (link) {
      return pathOf(link.url);
    });
    var links = guideLinks(query).filter(function (link) {
      return linkKey(link.url).indexOf('#') >= 0 || skipPaths.indexOf(pathOf(link.url)) < 0;
    });
    clear(els.guidesList);
    links.forEach(function (link) {
      var li = document.createElement('li');
      li.className = 'twrd-search__guides-item';
      var a = document.createElement('a');
      a.className = 'twrd-search__guide';
      a.setAttribute('href', link.url);
      var title = document.createElement('span');
      title.className = 'twrd-search__guide-title';
      title.textContent = link.title;
      var hint = document.createElement('span');
      hint.className = 'twrd-search__guide-hint';
      hint.textContent = link.hint;
      a.appendChild(title);
      a.appendChild(hint);
      li.appendChild(a);
      els.guidesList.appendChild(li);
    });
    els.guides.hidden = links.length === 0;
    return links;
  }

  /* ---------------- pages ----------------
     Site pages for the Pages group in the overlay and on /search.
     SITE_PAGES is a small static map from search words to real pages. It
     covers words the Shopify search index cannot match on title alone
     ("refund", "affiliate", "faq", "legal"). Every url was checked to load on
     the shop. /pages/affiliate redirects to /pages/carriers, so the affiliate
     words point there. Titles here also replace the shouty upper-case titles
     some pages have in the admin. */

  var PAGES_MAX = 6;
  var SITE_PAGES = [
    { title: 'Partners', url: '/pages/partners', keys: ['partner', 'partners', 'collab', 'collaboration'] },
    { title: 'Carriers, creator and affiliate program', url: '/pages/carriers', keys: ['affiliate', 'affiliates', 'carrier', 'carriers', 'creator', 'creators', 'ambassador'] },
    { title: 'Privacy policy', url: '/pages/privacy-policy', keys: ['privacy', 'gdpr', 'policy', 'policies', 'legal'] },
    { title: 'Terms of service', url: '/pages/terms-of-service', keys: ['terms', 'conditions', 'tos', 'policy', 'policies', 'legal'] },
    { title: 'Return policy', url: '/pages/return-policy', keys: ['return', 'returns', 'refund', 'refunds', 'policy', 'policies', 'legal'] },
    { title: 'Shipping', url: '/pages/shipping', keys: ['shipping', 'ship', 'delivery', 'policy', 'policies'] },
    { title: 'Cookie policy', url: '/pages/cookie-policy', keys: ['cookie', 'cookies', 'policy', 'policies', 'legal'] },
    { title: 'Withdrawal', url: '/pages/withdrawal', keys: ['withdraw', 'withdrawal', 'cancel', 'cancellation'] },
    { title: 'Data sharing opt-out', url: '/pages/data-sharing-opt-out', keys: ['opt', 'optout', 'sharing'] },
    { title: 'FAQ', url: '/pages/frequently-asked-questions', keys: ['faq', 'faqs', 'questions'] },
    { title: 'Help center', url: '/pages/help-center', keys: ['help', 'support'] },
    { title: 'Contact', url: '/pages/contact', keys: ['contact', 'email', 'support'] },
    { title: 'Track your order', url: '/pages/track-your-order', keys: ['track', 'tracking', 'order', 'orders'] },
    { title: 'Fabric guide', url: '/pages/fabric-guide', keys: ['fabric', 'gsm'] },
    { title: 'Fit guide', url: '/pages/how-should-an-oversized-t-shirt-fit', keys: ['fit', 'sizing'] },
    { title: 'Origin', url: '/pages/origin', keys: ['origin', 'about', 'story'] },
    { title: 'Founders', url: '/pages/founders', keys: ['founders', 'founder'] },
    { title: 'Music', url: '/pages/music', keys: ['music'] },
    { title: 'Imagery', url: '/pages/imagery', keys: ['imagery'] }
  ];

  /* Only same-site paths are rendered as links. */
  function safePath(url) {
    var value = String(url || '');
    if (!/^\/(?!\/)/.test(value)) return '';
    return value;
  }

  function pathOf(url) {
    return linkKey(url).split('#')[0].split('?')[0];
  }

  function pageWordHits(word, key) {
    if (word === key) return true;
    if (word.length >= 3 && key.indexOf(word) === 0) return true;
    return key.length >= 4 && word.indexOf(key) === 0;
  }

  function matchSitePages(query) {
    var qWords = String(query || '')
      .toLowerCase()
      .split(/[^a-z0-9]+/)
      .filter(Boolean);
    if (!qWords.length) return [];
    return SITE_PAGES.filter(function (page) {
      return page.keys.some(function (key) {
        return qWords.some(function (word) {
          return pageWordHits(word, key);
        });
      });
    }).map(function (page) {
      return { title: page.title, url: page.url };
    });
  }

  function siteTitle(url, fallback) {
    var path = pathOf(url);
    for (var i = 0; i < SITE_PAGES.length; i++) {
      if (SITE_PAGES[i].url === path) return SITE_PAGES[i].title;
    }
    return fallback;
  }

  /* Index results (pages, then articles) first, then static map top-ups.
     Deduped by path. */
  function pageLinks(query, found) {
    var seen = {};
    return (found || [])
      .filter(function (item) {
        return item && item.title && safePath(item.url);
      })
      .map(function (item) {
        return { title: siteTitle(item.url, item.title), url: item.url };
      })
      .concat(matchSitePages(query))
      .filter(function (link) {
        var path = pathOf(link.url);
        if (seen[path]) return false;
        seen[path] = true;
        return true;
      })
      .slice(0, PAGES_MAX);
  }

  /* Builds one row with textContent only, so titles never become markup. */
  function pageRow(link) {
    var li = document.createElement('li');
    li.className = 'twrd-pagelist__item';
    var a = document.createElement('a');
    a.className = 'twrd-pagelist__link';
    a.setAttribute('href', safePath(link.url));
    var title = document.createElement('span');
    title.className = 'twrd-pagelist__title';
    title.textContent = link.title;
    var path = document.createElement('span');
    path.className = 'twrd-pagelist__url';
    path.textContent = pathOf(link.url);
    a.appendChild(title);
    a.appendChild(path);
    li.appendChild(a);
    return li;
  }

  function renderPages(query, found) {
    if (!els.pages) return [];
    var links = pageLinks(query, found);
    clear(els.pagesList);
    links.forEach(function (link) {
      els.pagesList.appendChild(pageRow(link));
    });
    els.pages.hidden = links.length === 0;
    return links;
  }

  /* /search renders matching index pages server-side (sections/main-search.liquid).
     This adds the static map links the index cannot find. */
  function topUpSearchPage() {
    var box = document.querySelector('[data-twrd-search-page-list]');
    if (!box) return;
    var list = box.querySelector('[data-twrd-search-page-items]');
    if (!list) return;
    var have = Array.prototype.map.call(list.querySelectorAll('a[href]'), function (a) {
      return pathOf(a.getAttribute('href'));
    });
    var added = 0;
    matchSitePages(box.getAttribute('data-query') || '').forEach(function (link) {
      if (have.length + added >= PAGES_MAX) return;
      if (have.indexOf(pathOf(link.url)) >= 0) return;
      list.appendChild(pageRow(link));
      added++;
    });
    if (list.children.length) box.hidden = false;
  }

  /* ---------------- results ---------------- */

  function showIdle() {
    els.idle.hidden = false;
    els.results.hidden = true;
  }

  function showResults() {
    els.idle.hidden = true;
    els.results.hidden = false;
  }

  var SUGGEST_SHOWN = 6;

  function searchUrl(query) {
    return '/search?q=' + encodeURIComponent(query) + '&type=product,page,article&options[prefix]=last';
  }

  /* Suggestions are plain text links in lower case. Query suggestions run the
     search again in the panel; collection and product suggestions go to the
     collection or the product. */
  function buildSuggestions(query, queries, collections, products) {
    var items = queries
      .map(function (q) {
        return { text: q.text, kind: 'query' };
      })
      .concat(
        collections.map(function (c) {
          return { text: c.title, url: safePath(c.url), kind: 'link' };
        }),
        products.map(function (p) {
          return { text: p.title, url: safePath(p.url), kind: 'link' };
        })
      )
      .filter(function (item) {
        return item.text && (item.kind === 'query' || item.url);
      });
    var seen = {};
    return items
      .filter(function (item) {
        var key = String(item.text).toLowerCase();
        if (seen[key]) return false;
        seen[key] = true;
        return true;
      })
      .slice(0, SUGGEST_SHOWN);
  }

  function renderSuggestions(query, list) {
    clear(els.suggestions);
    list.forEach(function (item) {
      var text = String(item.text).toLowerCase();
      var li = document.createElement('li');
      li.className = 'twrd-search__links-item';
      var a = document.createElement('a');
      a.className = 'twrd-search__link twrd-search__sug';
      a.setAttribute('href', item.kind === 'query' ? searchUrl(item.text) : item.url);
      a.innerHTML = highlight(text, query.toLowerCase());
      if (item.kind === 'query') {
        a.addEventListener('click', function (event) {
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.button > 0) return;
          event.preventDefault();
          els.input.value = text;
          els.clear.hidden = false;
          run(text);
          try {
            els.input.focus();
          } catch (error) {
            /* focus can be refused */
          }
        });
      }
      li.appendChild(a);
      els.suggestions.appendChild(li);
    });
  }

  /* UPCOMING pieces go last; the order of the rest stays as the shop ranks it. */
  function upcomingLast(cards) {
    return cards
      .map(function (card, index) {
        return { card: card, index: index };
      })
      .sort(function (a, b) {
        return (a.card.upcoming ? 1 : 0) - (b.card.upcoming ? 1 : 0) || a.index - b.index;
      })
      .map(function (entry) {
        return entry.card;
      });
  }

  function announce(text) {
    if (!els.live) return;
    /* Clear first so the same text is read again for a new query. */
    els.live.textContent = '';
    window.setTimeout(function () {
      els.live.textContent = text;
    }, 60);
  }

  function setViewAll(query, show) {
    els.viewallQuery.textContent = query;
    els.viewall.setAttribute('href', searchUrl(query));
    if (els.viewallRow) els.viewallRow.hidden = !show;
  }

  function renderNone(query, show) {
    if (!els.none) return;
    els.none.hidden = !show;
    if (els.noneQuery) els.noneQuery.textContent = query; /* textContent: the query is never markup */
    if (els.hasProducts) els.hasProducts.hidden = show;
    if (els.sugcol) els.sugcol.hidden = show;
    if (els.results) els.results.classList.toggle('is-none', show);
  }

  function renderResults(query, data) {
    var resources = (data && data.resources && data.resources.results) || {};
    var products = resources.products || [];
    var queries = resources.queries || [];
    var collections = resources.collections || [];
    var pages = resources.pages || [];
    var articles = resources.articles || [];

    renderGuides(query, renderPages(query, pages.concat(articles)));

    var suggestions = products.length ? buildSuggestions(query, queries, collections, products) : [];
    renderSuggestions(query, suggestions);

    clear(els.products);
    var shown = upcomingLast(products.map(fromSuggest)).slice(0, 4);
    shown.forEach(function (cardData) {
      var card = buildCard(cardData);
      if (card) els.products.appendChild(card);
    });

    /* Phones show three suggestions (CSS), so announce what is on screen. */
    var sugShown = window.matchMedia && window.matchMedia('(max-width: 47.99em)').matches
      ? Math.min(suggestions.length, 3)
      : suggestions.length;
    renderNone(query, products.length === 0);
    setViewAll(query, products.length > 0);
    showResults();
    announce(
      shown.length
        ? shown.length + (shown.length === 1 ? ' product, ' : ' products, ') +
            sugShown + (sugShown === 1 ? ' suggestion' : ' suggestions')
        : 'No results'
    );
  }

  function run(query) {
    var trimmed = query.trim();
    if (trimmed.length < MIN_CHARS) {
      showIdle();
      return;
    }
    var id = ++reqId;
    var url =
      '/search/suggest.json?q=' +
      encodeURIComponent(trimmed) +
      '&resources[type]=product,collection,query,page,article&resources[limit]=8' +
      '&resources[options][unavailable_products]=last';

    suggestJson(trimmed.toLowerCase(), url)
      .then(function (data) {
        if (id !== reqId) return;
        renderResults(trimmed, data);
      })
      .catch(function (error) {
        if (id !== reqId) return;
        if (window.console && console.warn) console.warn('[twrd-search]', error);
        clear(els.suggestions);
        clear(els.products);
        renderGuides(trimmed, renderPages(trimmed, []));
        renderNone(trimmed, true);
        setViewAll(trimmed, false);
        showResults();
        announce('No results');
      });
  }

  /* ArrowDown / ArrowUp move through the links of the results. */
  function resultLinks() {
    if (!els.results || els.results.hidden) return [];
    return Array.prototype.filter.call(els.results.querySelectorAll('a[href]'), function (node) {
      return node.offsetParent !== null;
    });
  }

  function onArrow(event) {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return false;
    var list = resultLinks();
    if (!list.length) return false;
    var index = list.indexOf(document.activeElement);
    var onInput = document.activeElement === els.input;
    if (index < 0 && !onInput) return false;
    event.preventDefault();
    if (event.key === 'ArrowDown') {
      (index < 0 ? list[0] : list[Math.min(index + 1, list.length - 1)]).focus();
    } else if (index <= 0) {
      els.input.focus();
    } else {
      list[index - 1].focus();
    }
    return true;
  }

  /* ---------------- open / close ---------------- */

  function focusables() {
    return Array.prototype.filter.call(
      els.panel.querySelectorAll('a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])'),
      function (node) {
        return node.offsetParent !== null;
      }
    );
  }

  function onKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      close();
      return;
    }
    if (onArrow(event)) return;
    if (event.key !== 'Tab') return;
    var list = focusables();
    if (!list.length) return;
    var first = list[0];
    var last = list[list.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /* trigger: the header element that opened the panel. The header input
     never keeps focus (mousedown is cancelled, focusin blurs it), so
     document.activeElement is not a reliable return target. */
  function open(prefill, trigger) {
    if (!root || !root.hidden || inlineHost) return;
    lastFocus = trigger || document.activeElement;
    root.hidden = false;
    document.documentElement.classList.add('twrd-search-locked');
    window.requestAnimationFrame(function () {
      root.classList.add('is-open');
    });
    document.addEventListener('keydown', onKeydown, true);

    els.input.value = prefill || '';
    els.clear.hidden = !els.input.value;
    if (els.input.value.trim().length >= MIN_CHARS) run(els.input.value);
    else showIdle();

    renderRecentlyViewed();
    try {
      els.input.focus();
      els.input.setSelectionRange(els.input.value.length, els.input.value.length);
    } catch (error) {
      /* focus can be refused during the opening frame */
    }
  }

  function close() {
    if (!root || root.hidden) return;
    root.classList.remove('is-open');
    root.hidden = true;
    document.documentElement.classList.remove('twrd-search-locked');
    document.removeEventListener('keydown', onKeydown, true);
    showIdle();
    var target = lastFocus;
    lastFocus = null;
    if (target && target.isConnected && typeof target.focus === 'function') {
      /* The trigger is often the header search field: focusing it must not
         open the panel again (see the focusin handler). */
      restoring = true;
      try {
        target.focus({ preventScroll: true });
      } catch (error) {
        /* the trigger may be hidden or removed */
      }
      window.setTimeout(function () {
        restoring = false;
      }, 0);
    }
  }

  /* ---------------- inline mode ----------------
     The menu drawer borrows this panel instead of opening a second dialog.
     The panel node is moved into the drawer and moved back on return, so the
     markup, the suggest fetch and the card rendering stay the same code. */

  function openInline(host) {
    if (!root || !els.panel || !host) return false;
    /* The whole root moves, not just the panel: the input, the tokens and
       several body rules are scoped to .twrd-search and would stop applying
       if the panel were detached from it. */
    if (!panelHome) panelHome = root.parentNode;
    inlineHost = host;
    host.appendChild(root);
    root.hidden = false;
    root.classList.add('is-open', 'twrd-search--inline');
    els.panel.classList.add('twrd-search__panel--inline');
    /* Inline it is a state of the menu dialog, not a dialog of its own. */
    els.panel.removeAttribute('role');
    els.panel.removeAttribute('aria-modal');
    if (els.back) els.back.hidden = false;
    els.input.value = '';
    els.clear.hidden = true;
    reqId++;
    showIdle();
    renderRecentlyViewed();
    try {
      els.input.focus();
    } catch (error) {
      /* focus can be refused while the drawer animates */
    }
    return true;
  }

  function closeInline() {
    if (!inlineHost || !els.panel) return;
    if (panelHome) panelHome.appendChild(root);
    root.classList.remove('is-open', 'twrd-search--inline');
    root.hidden = true;
    els.panel.classList.remove('twrd-search__panel--inline');
    els.panel.setAttribute('role', 'dialog');
    els.panel.setAttribute('aria-modal', 'true');
    if (els.back) els.back.hidden = true;
    window.clearTimeout(timer);
    els.input.value = '';
    els.clear.hidden = true;
    reqId++;
    showIdle();
    inlineHost = null;
  }

  function typeInline(value) {
    if (!els.input) return;
    els.input.value = value;
    els.clear.hidden = !value;
    window.clearTimeout(timer);
    timer = window.setTimeout(function () {
      run(value);
    }, DEBOUNCE);
  }

  window.twrdSearchInline = {
    open: openInline,
    close: closeInline,
    type: typeInline,
    isOpen: function () {
      return !!inlineHost;
    },
    focusInput: function () {
      if (!els.input) return;
      try {
        els.input.focus();
      } catch (error) {
        /* focus can be refused */
      }
    }
  };

  /* ---------------- header triggers ---------------- */

  var TRIGGER_SELECTOR =
    '.twrd-hdr-search__input, .twrd-hdr-search__submit, .js-search-draw-icon a, ' +
    '.header-trigger__link--search, [data-a11y-dialog-show="modal-search"]';

  function isTrigger(target) {
    if (!target || !target.closest) return null;
    if (target.closest('[data-twrd-search]')) return null;
    /* The menu drawer drives its own field through the inline API. */
    if (target.closest('#modal-main-menu')) return null;
    return target.closest(TRIGGER_SELECTOR);
  }

  function intercept(event) {
    var trigger = isTrigger(event.target);
    if (!trigger) return;
    event.preventDefault();
    event.stopPropagation();
    if (event.stopImmediatePropagation) event.stopImmediatePropagation();
    /* Header has two facades (tablet and desktop). Read the one that was hit. */
    var form = trigger.closest('.twrd-hdr-search');
    var field = form ? form.querySelector('.twrd-hdr-search__input') : null;
    var prefill = field && field.value ? field.value : '';
    if (field) field.value = '';
    /* Focus goes back to the visible field of the hit facade, else the trigger. */
    open(prefill, field && field.offsetParent !== null ? field : trigger);
  }

  function bindTriggers() {
    document.addEventListener('mousedown', intercept, true);
    document.addEventListener('click', intercept, true);
    document.addEventListener(
      'focusin',
      function (event) {
        var trigger = isTrigger(event.target);
        if (!trigger || restoring) return;
        if (!root.hidden) return;
        event.target.blur();
        open('', trigger);
      },
      true
    );
    document.addEventListener(
      'keydown',
      function (event) {
        var trigger = isTrigger(event.target);
        if (!trigger) return;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          event.stopPropagation();
          open('', trigger);
        }
      },
      true
    );
  }

  /* ---------------- init ---------------- */

  function init() {
    topUpSearchPage();
    root = document.querySelector('[data-twrd-search]');
    if (!root || root.dataset.twrdSearchReady === 'true') return;
    root.dataset.twrdSearchReady = 'true';

    cardTemplate = document.getElementById('twrd-search-card-template');
    els = {
      panel: root.querySelector('[data-twrd-search-panel]'),
      form: root.querySelector('[data-twrd-search-form]'),
      input: root.querySelector('[data-twrd-search-input]'),
      clear: root.querySelector('[data-twrd-search-clear]'),
      idle: root.querySelector('[data-twrd-search-idle]'),
      results: root.querySelector('[data-twrd-search-results]'),
      suggestions: root.querySelector('[data-twrd-search-suggestions]'),
      products: root.querySelector('[data-twrd-search-products]'),
      guides: root.querySelector('[data-twrd-search-guides]'),
      guidesList: root.querySelector('[data-twrd-search-guides-list]'),
      pages: root.querySelector('[data-twrd-search-pages]'),
      pagesList: root.querySelector('[data-twrd-search-pages-list]'),
      sugcol: root.querySelector('[data-twrd-search-sugcol]'),
      hasProducts: root.querySelector('[data-twrd-search-hasproducts]'),
      none: root.querySelector('[data-twrd-search-none]'),
      noneQuery: root.querySelector('[data-twrd-search-none-q]'),
      live: root.querySelector('[data-twrd-search-live]'),
      viewall: root.querySelector('[data-twrd-search-viewall]'),
      viewallRow: root.querySelector('[data-twrd-search-viewall-row]'),
      viewallQuery: root.querySelector('[data-twrd-search-viewall-q]'),
      rv: root.querySelector('[data-twrd-search-rv]'),
      keep: root.querySelector('[data-twrd-search-keep]'),
      fallback: root.querySelector('[data-twrd-search-fallback]'),
      rail: root.querySelector('[data-twrd-search-rail]'),
      railPrev: root.querySelector('[data-twrd-search-rail-prev]'),
      railNext: root.querySelector('[data-twrd-search-rail-next]'),
      navs: root.querySelector('.twrd-search__navs'),
      back: root.querySelector('[data-twrd-search-back]')
    };

    if (!els.panel || !els.input) return;

    trackCurrentProduct();
    /* Consent can arrive after load (banner) or the privacy API can load late. */
    document.addEventListener('visitorConsentCollected', function () {
      window.setTimeout(trackCurrentProduct, 0);
    });
    if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function () {
        trackCurrentProduct();
      });
    }

    Array.prototype.forEach.call(root.querySelectorAll('[data-twrd-search-close]'), function (node) {
      node.addEventListener('click', close);
    });

    els.input.addEventListener('input', function () {
      var value = els.input.value;
      els.clear.hidden = !value;
      window.clearTimeout(timer);
      timer = window.setTimeout(function () {
        run(value);
      }, DEBOUNCE);
    });

    els.clear.addEventListener('click', function () {
      els.input.value = '';
      els.clear.hidden = true;
      reqId++;
      showIdle();
      els.input.focus();
    });

    els.form.addEventListener('submit', function (event) {
      if (!els.input.value.trim()) event.preventDefault();
    });

    if (els.railPrev) {
      els.railPrev.addEventListener('click', function () {
        els.rail.scrollBy({ left: -els.rail.clientWidth * 0.8, behavior: 'smooth' });
      });
      els.railNext.addEventListener('click', function () {
        els.rail.scrollBy({ left: els.rail.clientWidth * 0.8, behavior: 'smooth' });
      });
      els.rail.addEventListener('scroll', updateRailNavs);
      window.addEventListener('resize', updateRailNavs);
    }

    bindTriggers();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
